import React, {
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useLocation, useHistory } from 'react-router-dom';

import Logo from '@prisjakt/ui-logo';
import T from '@prisjakt/ui-translate';
import { AppContext } from '@prisjakt/ui-app-context';
import { withViewport } from '@prisjakt/ui-viewport';
import { Hideable } from '@regular_ui/lib/components/hideable';

import SearchBar from '../../components/search-bar';
import Viewport from '../../components/viewport';
import searchActions from '../search/actions/search';
import menuActions from '../navigation/actions/desktop';
import mobileMenuActions from '../navigation/actions/mobile-menu';
import NavigationMenu from '../navigation/index';
import MobileMenuButton from '../navigation/components/mobile-menu-button';
import ClassicSwitch from '../../components/classic-switch';
import NoScroll from '../../components/no-scroll';
import Suggestions from './components/suggestions';

import { AppState } from '../../reducers';

import * as S from './styles';

type OwnProps = {
  viewport: {
    top: number;
  };
};

type StateProps = {
  mobileMenuOpen?: boolean;
  megaMenuOpen?: boolean;
};

type DispatchProps = {
  searchFocus: () => void;
  searchBlur: () => void;
  closeMobileMenu: () => void;
  toggleMenu: (state: { open: boolean }) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const Header = ({
  mobileMenuOpen,
  megaMenuOpen = false,
  toggleMenu,
  closeMobileMenu,
  searchFocus,
  searchBlur,
  viewport,
}: Props) => {
  const location = useLocation();
  const history = useHistory();

  const megaMenuButtonRef = useRef<HTMLButtonElement>(null);

  const { t, market, renderMode } = useContext(AppContext);

  const [showSearchBar, setShowSearchBarState] = useState(false);
  const [query, setQueryState] = useState('');

  const toggleMegaMenu = useCallback(
    e => {
      // Prevent event from triggering an "outside click" for the menu
      e.stopPropagation();

      toggleMenu({ open: !megaMenuOpen });
    },
    [toggleMenu, megaMenuOpen]
  );

  const toggleSearchBar = useCallback(() => {
    setShowSearchBarState(!showSearchBar);

    if (mobileMenuOpen) {
      closeMobileMenu();
    }
  }, [showSearchBar, mobileMenuOpen, closeMobileMenu]);

  const clearQuery = useCallback(() => {
    setQueryState('');
  }, []);

  const closeSearchBar = useCallback(() => {
    setShowSearchBarState(false);
    clearQuery();
  }, [clearQuery]);

  const searchBarOnInputChange = useCallback(text => {
    setQueryState(text);
  }, []);

  const onCloseMegaMenu = useCallback(() => {
    if (megaMenuButtonRef.current) {
      megaMenuButtonRef.current.focus();
    }
  }, []);

  const searchFocused = /search=/.test(location.search);
  const isFrontpage = location.pathname === '/';
  const isSearchPage = location.pathname.indexOf('/search') === 0;

  const isSidebarOpen = mobileMenuOpen;
  const isScrolledDown = viewport && viewport.top > 90;
  const reserveSpaceForExpandedHeader = isFrontpage;
  const isExpandedOnMobile = isFrontpage && !isSidebarOpen && !isScrolledDown;
  const isMobileSearchOpen =
    (showSearchBar || isSearchPage || (isFrontpage && !isScrolledDown)) &&
    !isSidebarOpen;
  const menuButtonTitle = megaMenuOpen
    ? t('symbols', 'symbol.close')
    : t('header', 'categories.title');

  useEffect(() => {
    // Hide the search bar when changing route on mobile
    if (history.action === 'PUSH' && !searchFocused && !isSearchPage) {
      closeSearchBar();
    }
  }, [location, history, searchFocused, isSearchPage, closeSearchBar]);

  return (
    <>
      <S.FixedMargin
        mobileSearchOpen={isMobileSearchOpen || reserveSpaceForExpandedHeader}
        expandedOnMobile={isExpandedOnMobile || reserveSpaceForExpandedHeader}
      />
      <S.HeaderBar
        expandedOnMobile={isExpandedOnMobile}
        mobileSearchOpen={isMobileSearchOpen}
      >
        <S.Wrapper>
          <S.MenuColumn data-test="MobileMenuColumn">
            <S.MenuItem>
              <MobileMenuButton />
            </S.MenuItem>
          </S.MenuColumn>

          <S.LogoColumn>
            <S.MegaMenuItem title={menuButtonTitle} placement="right">
              <S.MegaMenuButton
                type="button"
                onClick={toggleMegaMenu}
                aria-label={menuButtonTitle}
                active={megaMenuOpen}
                aria-expanded={megaMenuOpen}
                aria-haspopup
                ref={megaMenuButtonRef}
                data-test="MegaMenuToggleButton"
              >
                {megaMenuOpen ? (
                  <S.CloseMegaMenuIcon size={30} />
                ) : (
                  <S.MegaMenuIcon size={30} />
                )}
              </S.MegaMenuButton>
            </S.MegaMenuItem>

            <S.LogoLink
              to="/"
              onClick={() => mobileMenuOpen && closeMobileMenu()}
            >
              <Logo
                market={market.code}
                companyName={t('meta', 'market.logo-alt')}
                styling={renderMode === 'beta' ? 'beta' : 'light'}
              />
              <Viewport tablet desktop>
                <S.PromoText>{t('header', 'logo.promo-text')}</S.PromoText>
              </Viewport>
            </S.LogoLink>
          </S.LogoColumn>

          <S.SearchColumn tablet desktop>
            <SearchBar
              autoFocus={isFrontpage}
              onFocusChange={searchFocus}
              onBlurChange={searchBlur}
              focused
              search={location.search}
              onInputChange={searchBarOnInputChange}
            />
          </S.SearchColumn>

          <S.RightColumn>
            <Viewport mobile>
              <Hideable visible={!isMobileSearchOpen}>
                <S.MenuItem>
                  <button
                    style={{ height: 50 }}
                    type="button"
                    onClick={toggleSearchBar}
                    aria-label={t('header', 'search.button')}
                  >
                    <S.SearchIcon size={30} />
                  </button>
                </S.MenuItem>
              </Hideable>
            </Viewport>

            <Viewport
              desktop
              style={{ marginRight: 'auto', marginLeft: 'auto' }}
            >
              <ClassicSwitch utmMedium="header">
                <S.ClassicSwitchLink>
                  <T domain="header">login.classic-link-word-wrap</T>
                </S.ClassicSwitchLink>
              </ClassicSwitch>
            </Viewport>

            <Viewport tablet>
              <S.MenuItem wideOnTablet>
                <MobileMenuButton handleOnClick={closeSearchBar} />
              </S.MenuItem>
            </Viewport>
          </S.RightColumn>
        </S.Wrapper>
        <S.SearchBox visible={isMobileSearchOpen}>
          <S.SearchColumn mobile>
            <SearchBar
              search={location.search}
              onFocusChange={searchFocus}
              onBlurChange={searchBlur}
              autoFocusOnExpand={showSearchBar && !isFrontpage}
              onInputChange={searchBarOnInputChange}
              mobile
            />

            {isExpandedOnMobile && (
              <S.PromoText>{t('header', 'logo.promo-text')}</S.PromoText>
            )}
          </S.SearchColumn>
        </S.SearchBox>

        {query && (
          <Suggestions
            query={query}
            searchInProps={false}
            onClose={clearQuery}
          />
        )}
        <NoScroll mobile enabled={!!query} />
      </S.HeaderBar>

      <NavigationMenu onCloseMegaMenu={onCloseMegaMenu} />
    </>
  );
};

const mapStateToProps = (state: AppState) => ({
  mobileMenuOpen: state.areas.navigation.mobileMenu.open,
  megaMenuOpen: state.areas.navigation.megaMenu.open,
});

const dispatchActions = {
  searchFocus: searchActions.searchFocus,
  searchBlur: searchActions.searchBlur,
  closeMobileMenu: mobileMenuActions.closeMenu,
  toggleMenu: menuActions.toggleMenu,
};

export default compose(
  withViewport,
  connect(mapStateToProps, dispatchActions)
)(Header);
